import {
  PartialAssuranceField,
  AssuranceListPortfolio,
  AssurancePermissionType,
  Organization,
  OrganizationAssurancePortfolio,
  UpdateStakeholderData
} from '@g17eco/types/assurance';
import { ManagementOrganizationInvitation, ManagementUser } from '@g17eco/types/onboarding';
import { OrganizationUserRole } from '@constants/user';
import { g17ecoApi, transformResponse } from './g17ecoApi';

enum AssuranceTags {
  Organization = 'assurance-organization',
  Users = 'assurance-users',
  Portfolio = 'assurance-portfolio',
  Assurance = 'assurance',
}

export type OrganizationUsers = {
  users: ManagementUser[];
  invitations: ManagementOrganizationInvitation[];
};

interface UpdatePartialAssuranceParams {
  portfolioId: string;
  utrvId: string;
  partialFields: PartialAssuranceField[];
}

export const assuranceApi = g17ecoApi
  .enhanceEndpoints({
    addTagTypes: Object.values(AssuranceTags),
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      // Existing endpoint
      updatePartialAssurance: builder.mutation<any, UpdatePartialAssuranceParams>({
        invalidatesTags: [AssuranceTags.Assurance],
        transformResponse,
        query: ({ portfolioId, utrvId, partialFields }) => ({
          url: `/assurers/portfolios/${portfolioId}/partial-assurance/${utrvId}`,
          method: 'post',
          data: { partialFields },
        }),
      }),

      // Endpoints from apps/assurance/api/assurance.ts
      getAssuranceOrganizationMembers: builder.query<OrganizationUsers, { organizationId: string }>({
        transformResponse,
        query: ({ organizationId }) => ({
          url: `/assurances/organization/${organizationId}/users`,
          method: 'get',
        }),
        providesTags: (_result, _error, { organizationId }) => {
          return [{ type: AssuranceTags.Users, id: organizationId }];
        },
      }),

      // May become deprecated if we support multiple assurance organizations per user
      // currently the relationship is one to one based on the organizationId field of users
      getUserOrganization: builder.query<Organization, void>({
        transformResponse,
        query: () => ({
          url: '/users/current/organization',
          method: 'get',
        }),
        providesTags: (result, _error) => {
          return [{ type: AssuranceTags.Organization, id: result?._id }];
        },
      }),

      createOrganizationUserOnboardings: builder.mutation<
        Organization,
        { organizationId: string; emails: string[]; permissions: OrganizationUserRole[] }
      >({
        transformResponse,
        query: ({ organizationId, emails, permissions }) => ({
          url: `/organizations/${organizationId}/users/onboard`,
          method: 'post',
          data: { emails, permissions },
        }),
        invalidatesTags: (_result, _error, { organizationId }) => [
          { type: AssuranceTags.Organization, id: organizationId },
          { type: AssuranceTags.Users, id: organizationId },
        ],
      }),

      updateUserOrganizationPermissions: builder.mutation<
        AssurancePermissionType<OrganizationUserRole>[],
        { organizationId: string; userId: string; permissions: OrganizationUserRole[] }
      >({
        transformResponse,
        query: ({ organizationId, userId, permissions }) => ({
          url: `/organizations/${organizationId}/users/${userId}/permissions`,
          method: 'patch',
          data: { permissions },
        }),
        invalidatesTags: (_result, _error, { organizationId }) => [
          { type: AssuranceTags.Organization, id: organizationId },
          { type: AssuranceTags.Users, id: organizationId },
        ],
      }),

      getAssurancePortfolio: builder.query<OrganizationAssurancePortfolio, string>({
        transformResponse,
        query: (id) => ({
          url: `/assurers/portfolios/${id}`,
          method: 'get',
        }),
        providesTags: (result, _error, id) => {
          return [{ type: AssuranceTags.Portfolio, id }];
        },
      }),

      updateAssurancePermissions: builder.mutation<void, { id: string; data: UpdateStakeholderData }>({
        transformResponse,
        query: ({ id, data }) => ({
          url: `/assurers/portfolios/${id}/permissions`,
          method: 'put',
          data,
        }),
        invalidatesTags: (_result, _error, { id }) => [{ type: AssuranceTags.Portfolio, id }],
      }),

      getAssurerPortfolios: builder.query<AssuranceListPortfolio[], void>({
        transformResponse,
        query: () => ({
          url: '/assurers/portfolios',
          method: 'get',
        }),
        providesTags: [AssuranceTags.Portfolio],
      }),
    }),
  });

export const {
  useUpdatePartialAssuranceMutation,
  useGetAssuranceOrganizationMembersQuery,
  useGetUserOrganizationQuery,
  useUpdateUserOrganizationPermissionsMutation,
  useCreateOrganizationUserOnboardingsMutation,
  useGetAssurancePortfolioQuery,
  useUpdateAssurancePermissionsMutation,
  useGetAssurerPortfoliosQuery,
} = assuranceApi;
